import React, { useState, useEffect } from 'react';
import { Button } from '../shared/Button';
import { useSpotify } from '../../context/SpotifyContext';
import './PlaylistWizard.css';

const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : 'http://localhost:3001/api';

interface GeneratedSong {
  artist: string;
  title: string;
  year: number | null;
  spotifyId?: string;
  spotifyUrl?: string;
  previewUrl?: string;
  imageUrl?: string;
  album?: string;
  duration?: number;
  spotifyUri?: string;
  youtubeVideoId?: string;
  youtubeUrl?: string;
  matched?: boolean;
  matchSource?: 'spotify' | 'youtube';
}

interface PlaylistWizardProps {
  onAddSongs: (songs: GeneratedSong[]) => void;
  onClose: () => void;
}

type WizardTab = 'ai' | 'playlist' | 'manual' | 'bulk';
type WizardStep = 'input' | 'preview' | 'adding';
type AIProvider = 'groq' | 'openRouter' | 'openai';

interface AIModel {
  id: string;
  name: string;
}

interface ProviderModels {
  groq: AIModel[];
  openRouter: AIModel[];
  openai: AIModel[];
}

export const PlaylistWizard: React.FC<PlaylistWizardProps> = ({ onAddSongs, onClose }) => {
  // Get Spotify access token for playlist imports
  const { accessToken } = useSpotify();
  
  // State
  const [activeTab, setActiveTab] = useState<WizardTab>('ai');
  const [step, setStep] = useState<WizardStep>('input');
  
  // AI states
  const [aiConfigured, setAiConfigured] = useState<boolean | null>(null);
  const [providers, setProviders] = useState<{ groq: boolean; openRouter: boolean; openai: boolean }>({ groq: false, openRouter: false, openai: false });
  const [selectedProvider, setSelectedProvider] = useState<AIProvider>('groq');
  const [providerModels, setProviderModels] = useState<ProviderModels>({ groq: [], openRouter: [], openai: [] });
  const [selectedModel, setSelectedModel] = useState('llama-3.3-70b-versatile');
  const [aiDescription, setAiDescription] = useState('');
  const [aiCount, setAiCount] = useState(25);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [groqApiKey, setGroqApiKey] = useState('');
  const [showAiConfig, setShowAiConfig] = useState(false);
  
  // Playlist import states
  const [playlistUrl, setPlaylistUrl] = useState('');
  const [playlistImporting, setPlaylistImporting] = useState(false);
  
  // Manual entry states
  const [manualArtist, setManualArtist] = useState('');
  const [manualTitle, setManualTitle] = useState('');
  const [manualYear, setManualYear] = useState('');
  
  // Bulk import states
  const [bulkText, setBulkText] = useState('');
  
  // Results
  const [generatedSongs, setGeneratedSongs] = useState<GeneratedSong[]>([]);
  const [selectedSongs, setSelectedSongs] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Check AI status on mount
  useEffect(() => {
    checkAIStatus();
  }, []);

  // Update selected model when provider changes
  useEffect(() => {
    const models = providerModels[selectedProvider];
    if (models && models.length > 0) {
      setSelectedModel(models[0].id);
    }
  }, [selectedProvider, providerModels]);

  const checkAIStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/ai/status`);
      const data = await response.json();
      setAiConfigured(data.configured);
      setProviders(data.providers || { groq: false, openRouter: false, openai: false });
      setProviderModels(data.availableModels || { groq: [], openRouter: [], openai: [] });
      if (data.provider) setSelectedProvider(data.provider);
      if (data.model) setSelectedModel(data.model);
      if (!data.configured) setShowAiConfig(true);
    } catch (err) {
      setAiConfigured(false);
      setShowAiConfig(true);
    }
  };

  const handleConfigureAI = async () => {
    if (!groqApiKey.trim()) return;
    
    try {
      const response = await fetch(`${API_URL}/ai/configure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ groqApiKey: groqApiKey.trim(), model: selectedModel })
      });
      
      if (response.ok) {
        setAiConfigured(true);
        setShowAiConfig(false);
        setGroqApiKey('');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to configure AI');
      }
    } catch (err) {
      setError('Failed to configure AI');
    }
  };

  const handleAIGenerate = async () => {
    if (!aiDescription.trim()) return;
    
    setAiGenerating(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/ai/generate-playlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: aiDescription.trim(),
          count: aiCount,
          service: 'spotify',
          provider: selectedProvider
        })
      });
      
      const data = await response.json();
      
      if (data.success && data.songs) {
        setGeneratedSongs(data.songs);
        setSelectedSongs(new Set(data.songs.map((_: any, i: number) => i)));
        setStep('preview');
      } else {
        setError(data.error || 'Failed to generate playlist');
      }
    } catch (err) {
      setError('Failed to connect to AI service');
    } finally {
      setAiGenerating(false);
    }
  };

  const handlePlaylistImport = async () => {
    if (!playlistUrl.trim()) return;
    
    setPlaylistImporting(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/spotify/import-playlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          playlistUrl: playlistUrl.trim(),
          userToken: accessToken || undefined 
        })
      });
      
      const data = await response.json();
      
      if (data.success && data.songs) {
        setGeneratedSongs(data.songs);
        setSelectedSongs(new Set(data.songs.map((_: any, i: number) => i)));
        setStep('preview');
      } else {
        setError(data.error || 'Failed to import playlist');
      }
    } catch (err) {
      setError('Failed to import playlist');
    } finally {
      setPlaylistImporting(false);
    }
  };

  const handleManualAdd = () => {
    if (!manualArtist.trim() || !manualTitle.trim()) return;
    
    const newSong: GeneratedSong = {
      artist: manualArtist.trim(),
      title: manualTitle.trim(),
      year: manualYear ? parseInt(manualYear) : null
    };
    
    setGeneratedSongs([...generatedSongs, newSong]);
    setSelectedSongs(prev => {
      const newSet = new Set(Array.from(prev));
      newSet.add(generatedSongs.length);
      return newSet;
    });
    
    // Clear inputs
    setManualArtist('');
    setManualTitle('');
    setManualYear('');
  };

  const handleBulkParse = () => {
    if (!bulkText.trim()) return;
    
    const lines = bulkText.trim().split('\n');
    const songs: GeneratedSong[] = [];
    
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      
      // Try to parse: Artist - Title (Year) or Artist - Title
      let match = trimmed.match(/^(.+?)\s*[-–—]\s*(.+?)\s*\((\d{4})\)\s*$/);
      if (match) {
        songs.push({ artist: match[1].trim(), title: match[2].trim(), year: parseInt(match[3]) });
        continue;
      }
      
      match = trimmed.match(/^(.+?)\s*[-–—]\s*(.+)$/);
      if (match) {
        songs.push({ artist: match[1].trim(), title: match[2].trim(), year: null });
        continue;
      }
      
      // If no match, treat whole line as title with unknown artist
      songs.push({ artist: 'Unknown', title: trimmed, year: null });
    }
    
    if (songs.length > 0) {
      setGeneratedSongs(songs);
      setSelectedSongs(new Set(songs.map((_, i) => i)));
      setStep('preview');
    } else {
      setError('No songs could be parsed from the text');
    }
  };

  const toggleSongSelection = (index: number) => {
    setSelectedSongs(prev => {
      const newSet = new Set(Array.from(prev));
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const selectAllSongs = () => {
    setSelectedSongs(new Set(generatedSongs.map((_, i) => i)));
  };

  const deselectAllSongs = () => {
    setSelectedSongs(new Set());
  };

  const handleAddSelected = () => {
    const songsToAdd = generatedSongs.filter((_, i) => selectedSongs.has(i));
    if (songsToAdd.length > 0) {
      onAddSongs(songsToAdd);
      onClose();
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'ai':
        return (
          <div className="wizard-tab-content ai-tab">
            {showAiConfig || !aiConfigured ? (
              <div className="ai-config">
                <h4>🔑 AI Configureren</h4>
                <p>
                  Om AI te gebruiken heb je een gratis Groq API key nodig.
                  <br />
                  <a href="https://console.groq.com/keys" target="_blank" rel="noopener noreferrer">
                    → Maak een gratis Groq API key aan
                  </a>
                </p>
                <input
                  type="password"
                  placeholder="Groq API Key"
                  value={groqApiKey}
                  onChange={(e) => setGroqApiKey(e.target.value)}
                />
                <Button onClick={handleConfigureAI} disabled={!groqApiKey.trim()}>
                  Opslaan
                </Button>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>Beschrijf je playlist</label>
                  <textarea
                    placeholder='bijv. "Nederlandse hits uit de jaren 80 en 90" of "Feesthits voor een bruiloft"'
                    value={aiDescription}
                    onChange={(e) => setAiDescription(e.target.value)}
                    rows={4}
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Aantal nummers</label>
                    <select value={aiCount} onChange={(e) => setAiCount(parseInt(e.target.value))}>
                      <option value={10}>10 nummers</option>
                      <option value={15}>15 nummers</option>
                      <option value={20}>20 nummers</option>
                      <option value={25}>25 nummers</option>
                      <option value={30}>30 nummers</option>
                      <option value={40}>40 nummers</option>
                      <option value={50}>50 nummers</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>AI Provider</label>
                    <select value={selectedProvider} onChange={(e) => setSelectedProvider(e.target.value as AIProvider)}>
                      {providers.groq && <option value="groq">Groq (Gratis)</option>}
                      {providers.openRouter && <option value="openRouter">OpenRouter</option>}
                      {providers.openai && <option value="openai">OpenAI</option>}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>AI Model</label>
                    <select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}>
                      {(providerModels[selectedProvider] || []).map(model => (
                        <option key={model.id} value={model.id}>{model.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <Button 
                  onClick={handleAIGenerate} 
                  disabled={!aiDescription.trim() || aiGenerating}
                  variant="primary"
                  className="generate-btn"
                >
                  {aiGenerating ? '🔄 Genereren...' : '✨ Genereer Playlist'}
                </Button>
                
                <button 
                  className="config-link"
                  onClick={() => setShowAiConfig(true)}
                >
                  ⚙️ AI opnieuw configureren
                </button>
              </>
            )}
          </div>
        );
        
      case 'playlist':
        return (
          <div className="wizard-tab-content playlist-tab">
            <div className="form-group">
              <label>Spotify Playlist URL</label>
              <input
                type="text"
                placeholder="https://open.spotify.com/playlist/..."
                value={playlistUrl}
                onChange={(e) => setPlaylistUrl(e.target.value)}
              />
            </div>
            <Button 
              onClick={handlePlaylistImport} 
              disabled={!playlistUrl.trim() || playlistImporting}
              variant="primary"
            >
              {playlistImporting ? '🔄 Importeren...' : '📥 Importeer Playlist'}
            </Button>
          </div>
        );
        
      case 'manual':
        return (
          <div className="wizard-tab-content manual-tab">
            <div className="form-row">
              <div className="form-group">
                <label>Artiest</label>
                <input
                  type="text"
                  placeholder="bijv. Queen"
                  value={manualArtist}
                  onChange={(e) => setManualArtist(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Titel</label>
                <input
                  type="text"
                  placeholder="bijv. Bohemian Rhapsody"
                  value={manualTitle}
                  onChange={(e) => setManualTitle(e.target.value)}
                />
              </div>
              <div className="form-group small">
                <label>Jaar</label>
                <input
                  type="number"
                  placeholder="1975"
                  value={manualYear}
                  onChange={(e) => setManualYear(e.target.value)}
                  min="1900"
                  max="2100"
                />
              </div>
            </div>
            <Button 
              onClick={handleManualAdd} 
              disabled={!manualArtist.trim() || !manualTitle.trim()}
              variant="secondary"
            >
              ➕ Voeg toe
            </Button>
            
            {generatedSongs.length > 0 && (
              <div className="manual-list">
                <h4>Toegevoegde nummers ({generatedSongs.length})</h4>
                {generatedSongs.map((song, i) => (
                  <div key={i} className="manual-song-item">
                    <span>{song.artist} - {song.title} {song.year ? `(${song.year})` : ''}</span>
                    <button onClick={() => {
                      setGeneratedSongs(prev => prev.filter((_, idx) => idx !== i));
                      setSelectedSongs(prev => {
                        const newSet = new Set(Array.from(prev).filter(idx => idx !== i).map(idx => idx > i ? idx - 1 : idx));
                        return newSet;
                      });
                    }}>✕</button>
                  </div>
                ))}
                <Button onClick={() => setStep('preview')} variant="primary">
                  Bekijk & Voeg toe →
                </Button>
              </div>
            )}
          </div>
        );
        
      case 'bulk':
        return (
          <div className="wizard-tab-content bulk-tab">
            <div className="form-group">
              <label>Plak nummers (één per regel)</label>
              <textarea
                placeholder={`Artiest - Titel (Jaar)\nQueen - Bohemian Rhapsody (1975)\nABBA - Dancing Queen (1976)\n\nOf zonder jaar:\nMichael Jackson - Thriller`}
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={10}
              />
            </div>
            <Button 
              onClick={handleBulkParse} 
              disabled={!bulkText.trim()}
              variant="primary"
            >
              📋 Verwerk tekst
            </Button>
          </div>
        );
    }
  };

  return (
    <div className="playlist-wizard-overlay">
      <div className="playlist-wizard">
        <div className="wizard-header">
          <h2>✨ Nummers Toevoegen</h2>
          <span className="beta-badge">BETA</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {error && (
          <div className="wizard-error">
            ❌ {error}
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {step === 'input' ? (
          <>
            <div className="wizard-tabs">
              <button 
                className={`wizard-tab ${activeTab === 'ai' ? 'active' : ''}`}
                onClick={() => setActiveTab('ai')}
              >
                <span className="tab-icon">🤖</span>
                <span className="tab-label">AI</span>
                <span className="tab-desc">Genereer met AI</span>
              </button>
              <button 
                className={`wizard-tab ${activeTab === 'playlist' ? 'active' : ''}`}
                onClick={() => setActiveTab('playlist')}
              >
                <span className="tab-icon">📋</span>
                <span className="tab-label">Playlist</span>
                <span className="tab-desc">Importeer van Spotify</span>
              </button>
              <button 
                className={`wizard-tab ${activeTab === 'manual' ? 'active' : ''}`}
                onClick={() => setActiveTab('manual')}
              >
                <span className="tab-icon">✏️</span>
                <span className="tab-label">Handmatig</span>
                <span className="tab-desc">Eén voor één</span>
              </button>
              <button 
                className={`wizard-tab ${activeTab === 'bulk' ? 'active' : ''}`}
                onClick={() => setActiveTab('bulk')}
              >
                <span className="tab-icon">📝</span>
                <span className="tab-label">Bulk</span>
                <span className="tab-desc">Plak tekst</span>
              </button>
            </div>

            {renderTabContent()}
          </>
        ) : (
          <div className="preview-step">
            <div className="preview-header">
              <h3>📋 {generatedSongs.length} nummers gevonden ({generatedSongs.filter(s => s.matchSource === 'spotify').length} Spotify, {generatedSongs.filter(s => s.matchSource === 'youtube').length} YouTube)</h3>
              <div className="preview-actions">
                <button className="action-btn" onClick={selectAllSongs}>Alles selecteren</button>
                <button className="action-btn" onClick={deselectAllSongs}>Niets selecteren</button>
                <button className="action-btn" onClick={() => { setStep('input'); setGeneratedSongs([]); }}>
                  ← Terug
                </button>
              </div>
            </div>
            
            <div className="preview-list">
              {generatedSongs.map((song, index) => (
                <div 
                  key={index} 
                  className={`preview-song ${selectedSongs.has(index) ? 'selected' : ''} ${song.matched ? 'matched' : 'unmatched'}`}
                  onClick={() => toggleSongSelection(index)}
                >
                  <input
                    type="checkbox"
                    checked={selectedSongs.has(index)}
                    onChange={() => toggleSongSelection(index)}
                  />
                  {song.imageUrl ? (
                    <img src={song.imageUrl} alt={song.album || song.title} className="song-thumb" />
                  ) : (
                    <div className="song-thumb placeholder">
                      {song.matched ? '🎵' : '❓'}
                    </div>
                  )}
                  <div className="song-info">
                    <span className="song-title">{song.title}</span>
                    <span className="song-artist">{song.artist}</span>
                    {song.album && <span className="song-album">{song.album}</span>}
                  </div>
                  <div className="song-meta">
                    {song.year && <span className="song-year">{song.year}</span>}
                    <span className={`match-status ${song.matched ? (song.matchSource === 'spotify' ? 'matched' : 'youtube') : 'unmatched'}`}>
                      {song.matched 
                        ? (song.matchSource === 'spotify' ? '✓ Spotify' : '▶ YouTube')
                        : '⚠ Niet gevonden'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="preview-footer">
              <Button 
                onClick={handleAddSelected} 
                disabled={selectedSongs.size === 0}
                variant="success"
              >
                ✓ Voeg {selectedSongs.size} nummers toe aan ronde
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
